/**
 * ScrollPane.js
 * Created on 10-okt-2013
 * 
 * A wrapper class for scrollPanes
 * Works with createjs and exported skins from flash.
 * 
 * See software lib src/ranj/test/components/test_buttons.html for example implementation with createjs
 * 
 * @author Ivo Swartjes for Ranj
 **/

import Rectangle from "../../geom/Rectangle";
import EventDispatcher from "../../util/EventDispatcher";
import { isNull } from "../../util/TypeUtil";
import DragInertia from "./DragInertia";
import ScrollBar from "./ScrollBar";

export default class ScrollPane {
    /**
     * @constructor
     * @param {Rectangle} viewport Rectangle determining the viewport
     * @param {createjs.MovieClip} hScrollBarSkin
     * @param {createjs.MovieClip} vScrollBarSkin 
     * @param {createjs.MovieClip} contentContainer Container that content will be added to. This is the content that will be masked by the scroll pane
     * @param {createjs.Shape} contentMask optional mask shape. If not provided, this component will create one based on the viewport
     * @param {boolean} dragEnabled Whether or not dragging the scroll pane is enabled (default = true)
     * @param {boolean} scrollWheelEnabled Whether or scrolling by scroll wheel is enabled (default = true)
     * @param {number} scrollBarPadding Padding between the handle and scroll area
     * @param {createjs.Stage} stage 
     */
    constructor(viewport, hScrollBarSkin, vScrollBarSkin, contentContainer, contentMask, dragEnabled, scrollWheelEnabled, scrollBarPadding, stage) {
        EventDispatcher.setAsDispatcher(this);

        this._contentContainer = contentContainer;

        // initialize inertia
        this._inertia = new DragInertia();
        this._onTickInertiaBound = this._onTickInertia.bind(this);
        this.inertiaEnabled = true;
        this._dragEnabled = (!isNull(dragEnabled) ? dragEnabled : true);
        this._scrollWheelEnabled = (!isNull(scrollWheelEnabled) ? scrollWheelEnabled : true);

        // If stage was passed as an argument, use it, otherwise try to derive stage from the content container.
        // Getting the stage from the content container only seems to work in Flash 2015, and no longer in Animate CC.
        this._stage = stage;
        if (!this._stage) {
            this._stage = this._contentContainer.stage;
        }

        // A rectangle {x, y, w, h} defining the viewport. 
        this._viewport = viewport;
        this._contentContainerInitialX = contentContainer.x;
        this._contentContainerInitialY = contentContainer.y;

        this._bounds = new Rectangle(0, 0, 0, 0);

        this._onMouseDownBound = this._onMouseDown.bind(this);
        this._onMouseDraggingMoveBound = this._onMouseDraggingMove.bind(this);
        this._onMouseClickBound = this._onMouseClick.bind(this);
        this._onMouseUpBound = this._onMouseUp.bind(this);

        this._showScrollBars = true;
        this._scrollStarted = false;
        this._preventClick = false;
        this._clickMargin = 3;

        // (optional) scroll bar skins
        if (!isNull(hScrollBarSkin)) {
            this._hScrollBar = new ScrollBar(hScrollBarSkin, false, scrollBarPadding);
            this._hScrollBarListener = this._handleHScrollBarChange.bind(this);
            this._hScrollBar.addEventListener("change", this._hScrollBarListener);
        }

        if (!isNull(vScrollBarSkin)) {
            this._vScrollBar = new ScrollBar(vScrollBarSkin, true, scrollBarPadding);
            this._vScrollBarListener = this._handleVScrollBarChange.bind(this);
            this._vScrollBar.addEventListener("change", this._vScrollBarListener);
        }

        this._autoHideHScrollBar = ScrollPane.autoHideHScrollBarDefault;
        this._autoHideVScrollBar = ScrollPane.autoHideVScrollBarDefault;

        this._scrollSpeed = 50;
        if (this._scrollWheelEnabled) {
            //listen to parent of canvas because in a multiple canvas setup, our canvas may not be the top one
            this._mouseWheelTarget = this._stage.canvas.parentElement;
            this._onMouseWheelBound = this._onMouseWheel.bind(this);

            // http://www.sitepoint.com/html5-javascript-mouse-wheel/
            // IE9, Chrome, Safari, Opera
            this._mouseWheelTarget.addEventListener("mousewheel", this._onMouseWheelBound, false);
            // Firefox
            this._mouseWheelTarget.addEventListener("DOMMouseScroll", this._onMouseWheelBound, false);
        }

        // Create mask if not already in asset
        if (!isNull(this._contentContainer.mask)) {
            contentMask = this._contentContainer.mask;
        }

        if (!isNull(contentMask)) {
            this._contentMask = contentMask;
        } else {
            this._contentMask = new createjs.Shape();
            this._contentMask.x = viewport.x;
            this._contentMask.y = viewport.y;
            this._contentMask.graphics.beginFill("#000000");
            this._contentMask.graphics.drawRect(0, 0, viewport.width, viewport.height);
        }
        this._contentMask.cache(0, 0, viewport.width, viewport.height);
        this._contentContainer.mask = this._contentMask;

        if (this._dragEnabled) {
            this._setupBackgroundDragging();
            this._setupDragging();
        }

        this.update();
    }

    destroy() {
        createjs.Ticker.removeEventListener('tick', this._onTickInertiaBound);

        this._contentContainer.removeEventListener("mousedown", this._onMouseDownBound);
        this._contentContainer.removeEventListener("pressmove", this._onMouseDraggingMoveBound);

        this._contentContainer.removeEventListener("click", this._onMouseClickBound);
        this._contentContainer.removeEventListener("pressup", this._onMouseUpBound);

        this.removeAllEventListeners();

        if (!isNull(this._hScrollBar)) {
            this._hScrollBar.removeEventListener("change", this._hScrollBarListener);
            this._hScrollBar.destroy();
        }
        if (!isNull(this._vScrollBar)) {
            this._vScrollBar.removeEventListener("change", this._vScrollBarListener);
            this._vScrollBar.destroy();
        }
        this._hScrollBar = null;
        this._vScrollBar = null;
        this._hScrollBarListener = null;
        this._vScrollBarListener = null;

        if (this._scrollWheelEnabled) {
            // IE9, Chrome, Safari, Opera
            this._mouseWheelTarget.removeEventListener("mousewheel", this._onMouseWheelBound, false);
            // Firefox
            this._mouseWheelTarget.removeEventListener("DOMMouseScroll", this._onMouseWheelBound, false);

            this._stage = null;
        }
    }

    setViewPort(viewport) {
        this._viewport = viewport;
        this._contentMask.x = viewport.x;
        this._contentMask.y = viewport.y;
        this._contentMask.graphics.clear();
        this._contentMask.graphics.beginFill("#000000");
        this._contentMask.graphics.drawRect(0, 0, viewport.width, viewport.height);
        this._contentContainer.mask = this._contentMask;
        this.setScrollPosition(viewport.x, viewport.y);
    }

    _setupDragging() {
        this._contentContainer.addEventListener("mousedown", this._onMouseDownBound, true);
    }

    /**
     * Creates a shape behind the content. This enables dragging in the gaps between items.
     * This shape is updated in the `_updateBgDragContainer` method to fit the viewport and content size.
     */
    _setupBackgroundDragging() {
        this._bgDragContainer = new createjs.Container();
        this._bgDragContainer.name = "bgDragContainer"; // useful for debugging (shows up in child.name prop)

        this._bgHitArea = new createjs.Shape();
        this._bgHitArea.name = "bgHitArea"; // useful for debugging (shows up in child.name prop)

        this._contentContainer.addChildAt(this._bgDragContainer, 0);
        this._bgDragContainer.hitArea = this._bgHitArea;
    }

    _updateBarVisibility() {
        if (!isNull(this._hScrollBar)) {
            if (this._viewport.width > this._bounds.width) {
                this._hScrollBar.skin.visible = !this._autoHideHScrollBar;
            } else {
                this._hScrollBar.skin.visible = this._showScrollBars;
            }
        }
        if (!isNull(this._vScrollBar)) {
            if (this._viewport.height > this._bounds.height) {
                this._vScrollBar.skin.dragButton.visible = !this._autoHideVScrollBar;
                this._vScrollBar.skin.dragArea.visible = !this._autoHideVScrollBar;
            } else {
                this._vScrollBar.skin.dragButton.visible = this._showScrollBars;
                this._vScrollBar.skin.dragArea.visible = this._showScrollBars;
            }
        }
    }

    _updateBgDragContainer() {
        if (!this._bgHitArea) {
            // Ignore if bg hit area hasn't been set-up yet or dragging is disabled anyway
            return;
        }

        this._bgHitArea.graphics.clear();
        this._bgHitArea.graphics
            .beginFill('rgba(255,0,0,0.4)')
            .drawRect(
                Math.min(this._bounds.x, this._viewport.x),
                Math.min(this._bounds.y, this._viewport.y),
                Math.max(this._bounds.width, this._viewport.width),
                Math.max(this._bounds.height, this._viewport.height)
            );

        // Ensure that the container and hit area are at the top left
        this._bgDragContainer.y = 0;
        this._bgHitArea.y = 0;

        // Ensure that the bg hit area is at the bottom and is not overlapping any buttons
        this._contentContainer.setChildIndex(this._bgDragContainer, 0);
    }

    isVScrollBarVisible() {
        return this._vScrollBar && this._vScrollBar.skin.visible;
    }

    isHScrollBarVisible() {
        return this._vScrollBar && this._vScrollBar.skin.visible;
    }

    showScrollBars(aValue) {
        this._showScrollBars = aValue;
        this._updateBarVisibility();
    }

    setAutoHideHScrollBar(autoHideHScrollBar) {
        this._autoHideHScrollBar = autoHideHScrollBar;
        this._updateBarVisibility();
    }

    setAutoHideVScrollBar(autoHideVScrollBar) {
        this._autoHideVScrollBar = autoHideVScrollBar;
        this._updateBarVisibility();
    }

    setClickMargin(clickMargin) {
        this._clickMargin = clickMargin;
    }

    getClickMargin() {
        return this._clickMargin;
    }

    setScrollSpeed(scrollSpeed) {
        this._scrollSpeed = scrollSpeed;
    }

    getScrollSpeed() {
        return this._scrollSpeed;
    }

    setBounds(x, y, width, height) {
        this._bounds.x = x;
        this._bounds.y = y;
        this._bounds.width = width;
        this._bounds.height = height;
        this._updateBarVisibility();
    }

    setHeightBound(height) {
        this._bounds.height = height;
        this._updateBarVisibility();
        this._updateBgDragContainer();
    }

    /**
     * Should be called after the contents have been changed, so that the component can update its scrollbars etc.
     * Perhaps better to not allow direct manipulation of the scroll content, but have a setContent() function instead as in the AS3 version.
     */
    update(bounds) {
        this._bounds = bounds || this.determineBounds() || this._bounds;

        this.setScrollPosition(this._contentContainer.x, this._contentContainer.y);
        this._updateBgDragContainer();
        this._updateBarVisibility();
    }

    resetPosition() {
        this.setScrollPosition(this._contentContainerInitialX, this._contentContainerInitialY);
    }

    /**
     * @param hScrollPos horizontal scroll position in pixels (negative)
     * @param vScrollPos vertical scroll position in pixels (negative)
     */
    setScrollPosition(hScrollPos, vScrollPos, stopInertia = true) {
        if (stopInertia) {
            this._stopInertia();
        }
        vScrollPos -= this._contentContainerInitialY;
        hScrollPos -= this._contentContainerInitialX;

        //console.log("vScrollPos " + vScrollPos);

        // Keep horizontal scroll pos within bounds
        if (this._viewport.width > this._bounds.width) {
            // Not scrollable
            hScrollPos = 0;
        } else {
            // Compensate for bounds
            if (hScrollPos > 0) {
                hScrollPos = 0;
            }

            if (hScrollPos < - this._bounds.width + this._viewport.width) {
                hScrollPos = - this._bounds.width + this._viewport.width;
            }
        }

        // Keep vertical scroll pos within bounds
        if (this._viewport.height > this._bounds.height) {
            // Not scrollable
            vScrollPos = 0;
        } else {
            // Compensate for bounds
            if (vScrollPos > 0) {
                vScrollPos = 0;
            }

            if (vScrollPos < - this._bounds.height + this._viewport.height) {
                vScrollPos = - this._bounds.height + this._viewport.height;
            }
        }

        const hScrollPosPct = hScrollPos === 0 ? 0 : hScrollPos / (this._viewport.width - this._bounds.width);
        const vScrollPosPct = vScrollPos === 0 ? 0 : vScrollPos / (this._viewport.height - this._bounds.height);

        this._contentContainer.x = this._contentContainerInitialX + hScrollPos;
        this._contentContainer.y = this._contentContainerInitialY + vScrollPos;

        this._hScrollPos = hScrollPos;
        this._vScrollPos = vScrollPos;

        if (!isNull(this._hScrollBar)) {
            this._hScrollBar.setValue(hScrollPosPct);
        }
        if (!isNull(this._vScrollBar)) {
            this._vScrollBar.setValue(vScrollPosPct);
        }

        this.dispatchPositionUpdate();
    }

    /**
     * This is a separate method so we can easily override it
     */
    dispatchPositionUpdate() {
        this.dispatchEvent(ScrollPane.SCROLL_POSITION_UPDATE);
    }

    determineBounds() {
        let rect = this._contentContainer.getBounds();//will result in null for Containers containing only Shapes

        // NominalBounds is set as property of the prototype in the exported library js, so this works only for skins from the exported library
        // Because it is set as a property on the prototype it is shared between instances, we clone it here to prevent sharing
        rect = rect || this._contentContainer.nominalBounds?.clone();

        if (!rect) {
            return new createjs.Rectangle(0, 0, 0, 0);
        }

        return rect;
    }

    getVisibleArea() {
        const area = new createjs.Rectangle();
        area.x = this._contentContainerInitialX - this._contentContainer.x;
        area.y = this._contentContainerInitialY - this._contentContainer.y;
        area.width = this._viewport.width;
        area.height = this._viewport.height;
        return area;
    }

    _handleHScrollBarChange() {
        const posPct = this._hScrollBar.getValue();
        const hScrollPos = this._contentContainerInitialX + posPct * (this._viewport.width - this._bounds.width);
        this.setScrollPosition(hScrollPos, this._contentContainer.y);
    }

    _handleVScrollBarChange() {
        const posPct = this._vScrollBar.getValue();
        const vScrollPos = this._contentContainerInitialY + posPct * (this._viewport.height - this._bounds.height);
        this.setScrollPosition(this._contentContainer.x, vScrollPos);
    }

    _onMouseDown({ rawX, rawY }) {
        // use raw x/y to get coordinates outside the container and outside the canvas while dragging
        this._globalPtStart = new createjs.Point(rawX, rawY);
        this._localPtStart = this._contentContainer.globalToLocal(rawX, rawY);
        this._moved = false;
        this._stopInertia();
        this._contentContainer.addEventListener("pressmove", this._onMouseDraggingMoveBound, true);
    }

    _onMouseDraggingMove({ rawX, rawY }) {
        if (!this._moved) {
            this._moved = true;
            this._contentContainer.addEventListener("click", this._onMouseClickBound, true);
            this._contentContainer.addEventListener("pressup", this._onMouseUpBound, true);
        }
        // use raw x/y to get coordinates outside the container and outside the canvas while dragging
        const localPt = this._contentContainer.globalToLocal(rawX, rawY);
        const dx = localPt.x - this._localPtStart.x;
        const dy = localPt.y - this._localPtStart.y;
        const globalDx = rawX - this._globalPtStart.x;
        const globalDy = rawY - this._globalPtStart.y;
        //we need a minimum offset before starting to scroll and preventing clicks
        if (!this._scrollStarted) {
            if (Math.abs(globalDx) >= this._clickMargin || Math.abs(globalDy) >= this._clickMargin) {
                this._scrollStarted = true;
                this._preventClick = true;
                this._initialDx = dx;
                this._initialDy = dy;
                this.dispatchEvent(ScrollPane.SCROLL_STARTED);
            }
        } else {
            // NB: subtract the initial distance to remove a sudden jerk when the drag move is detected
            // from the minimum offset to start scrolling
            const newX = this._contentContainer.x + dx - this._initialDx;
            const newY = this._contentContainer.y + dy - this._initialDy;
            this.setScrollPosition(newX, newY);
        }
    }

    setX(aX) {
        this.setScrollPosition(aX, this._contentContainer.y);
    }

    setY(aY) {
        this.setScrollPosition(this._contentContainer.x, aY);
    }

    getX() {
        return this._hScrollPos;
    }

    getY() {
        return this._vScrollPos;
    }

    _onMouseClick(event) {
        if (this._preventClick) {
            this._preventClick = false;
            // prevent clicks when dragging
            event.stopImmediatePropagation();
        }
    }

    _onMouseUp(event) {
        this._contentContainer.removeEventListener("pressmove", this._onMouseDraggingMoveBound, true);
        this._contentContainer.removeEventListener("click", this._onMouseClickBound, true);
        this._contentContainer.removeEventListener("pressup", this._onMouseUpBound, true);

        if (this._scrollStarted) {
            this._scrollStarted = false;
            this._startInertia();
            this.dispatchEvent(ScrollPane.SCROLL_ENDED);
        }
    }

    isScrollWheelEnabled() {
        return this._scrollWheelEnabled;
    }

    _onMouseWheel({ wheelDelta, detail }) {
        const mousePos = this._contentContainer.parent.globalToLocal(this._stage.mouseX, this._stage.mouseY);
        const inViewport = (mousePos.x >= this._viewport.x && mousePos.x <= (this._viewport.x + this._viewport.width)
            && mousePos.y >= this._viewport.y && mousePos.y <= (this._viewport.y + this._viewport.height));

        if (inViewport) {
            // cross-browser wheel delta
            const delta = Math.max(-1, Math.min(1, (wheelDelta || -detail)));
            this.setScrollPosition(this._contentContainer.x, this._contentContainer.y + delta * this._scrollSpeed);
        }
    }

    get inertiaEnabled() {
        return this._inertiaEnabled;
    }

    set inertiaEnabled(value) {
        if (this._inertiaEnabled === value) {
            return;
        }

        if (value) {
            createjs.Ticker.addEventListener('tick', this._onTickInertiaBound);
        } else {
            createjs.Ticker.removeEventListener('tick', this._onTickInertiaBound);
            this._inertia.stopInertia();
        }

        this._inertiaEnabled = value;
    }

    set decelerationRate(value) {
        this._inertia.decelerationRate = value;
    }

    get decelerationRate() {
        return this._inertia.decelerationRate;
    }

    _startInertia() {
        if (this._inertiaEnabled) {
            this._inertia.startInertia();
        }
    }

    _stopInertia() {
        if (this._inertiaEnabled) {
            this._inertia.stopInertia();
        }
    }

    _onTickInertia({ delta }) {
        if (!this._scrollStarted && !this._inertia.isInertiaActive) {
            // NB: do not update if not scrolling and if inertia is not active
            return;
        }

        const deltaSeconds = delta / 1000;
        this._inertia.update(this.getX(), this.getY(), deltaSeconds);
        if (this._inertia.isInertiaActive) {
            this.setScrollPosition(this._inertia.x, this._inertia.y, false);
        }
    }
}

ScrollPane.autoHideHScrollBarDefault = true;
ScrollPane.autoHideVScrollBarDefault = true;

ScrollPane.SCROLL_STARTED = "scroll_started";
ScrollPane.SCROLL_ENDED = "scroll_ended";
ScrollPane.SCROLL_POSITION_UPDATE = "scroll_position_update";